import api from './client';

/** AI generation endpoints call Claude and can take 30-90s; use a longer timeout than the default 30s. */
const AI_TIMEOUT = 120_000;

import type {
  PropertyLookupRequest,
  PropertyLookupResponse,
  CreateAnalysisRequest,
  AnalysisResponse,
  AnalysisListResponse,
  RenovationInputs,
  CompProperty,
  AddCompRequest,
  SnapshotListResponse,
  SnapshotDetail,
  BenchmarkResponse,
  PortfolioSummary,
  DealNarrative,
  DocumentInfo,
  AnalysisPhotoInfo,
  PropertyConditionScore,
  AICompAnalysis,
  PermitZoningAnalysis,
  MarketIntelligenceAnalysis,
  CompClassificationResult,
  CompCategory,
  ARVEstimation,
  PhotoScopeEstimation,
  BuildabilityResponse,
  CostEstimateResult,
  FlipIntelligenceStats,
} from '../types/analysis';

export async function lookupProperty(req: PropertyLookupRequest): Promise<PropertyLookupResponse> {
  const { data } = await api.post<PropertyLookupResponse>('/analyses/lookup-property', req);
  return data;
}

export async function createAnalysis(req: CreateAnalysisRequest): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>('/analyses/', req);
  return data;
}

export async function listAnalyses(archived?: boolean): Promise<AnalysisListResponse> {
  const params = archived != null ? { archived } : {};
  const { data } = await api.get<AnalysisListResponse>('/analyses/', { params });
  return data;
}

export async function getAnalysis(id: string): Promise<AnalysisResponse> {
  const { data } = await api.get<AnalysisResponse>(`/analyses/${id}`);
  return data;
}

export async function deleteAnalysis(id: string): Promise<void> {
  await api.delete(`/analyses/${id}`);
}

export async function generateEstimate(
  id: string,
  inputs: RenovationInputs,
): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>(`/analyses/${id}/estimate`, {
    renovation_inputs: inputs,
  });
  return data;
}

export async function fetchComps(id: string): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>(`/analyses/${id}/fetch-comps`);
  return data;
}

export async function addComp(id: string, comp: AddCompRequest): Promise<CompProperty> {
  const { data } = await api.post<CompProperty>(`/analyses/${id}/comps`, comp);
  return data;
}

export async function removeComp(id: string, compId: string): Promise<void> {
  await api.delete(`/analyses/${id}/comps/${compId}`);
}

export async function toggleCompExcluded(id: string, compId: string): Promise<void> {
  await api.patch(`/analyses/${id}/comps/${compId}/toggle-exclude`);
}

// ── Comp Classification ──

export async function classifyComps(id: string): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>(`/analyses/${id}/classify-comps`, null, {
    timeout: AI_TIMEOUT,
  });
  return data;
}

export async function updateCompCategory(
  id: string,
  compId: string,
  category: CompCategory,
): Promise<AnalysisResponse> {
  const { data } = await api.patch<AnalysisResponse>(`/analyses/${id}/comps/${compId}/category`, {
    category,
  });
  return data;
}

// ── ARV Estimation ──

export async function estimateARV(id: string): Promise<ARVEstimation> {
  const { data } = await api.post<{ arv_estimation: ARVEstimation }>(
    `/analyses/${id}/arv-estimate`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.arv_estimation;
}

export async function saveFlipAnalysis(
  id: string,
  data: Record<string, unknown>,
): Promise<AnalysisResponse> {
  const { data: result } = await api.post<AnalysisResponse>(
    `/analyses/${id}/save-flip-analysis`,
    data,
  );
  return result;
}

export async function recalculateInvestment(
  id: string,
  purchasePrice?: number,
): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>(
    `/analyses/${id}/recalculate-investment`,
    null,
    { params: purchasePrice != null ? { purchase_price: purchasePrice } : {} },
  );
  return data;
}

export async function updateRenovationInputs(
  id: string,
  inputs: RenovationInputs,
): Promise<AnalysisResponse> {
  const { data } = await api.put<AnalysisResponse>(`/analyses/${id}/estimate`, {
    renovation_inputs: inputs,
  });
  return data;
}

// ── Snapshots ──

export async function listSnapshots(id: string): Promise<SnapshotListResponse> {
  const { data } = await api.get<SnapshotListResponse>(`/analyses/${id}/snapshots`);
  return data;
}

export async function getSnapshot(id: string, snapshotId: string): Promise<SnapshotDetail> {
  const { data } = await api.get<SnapshotDetail>(`/analyses/${id}/snapshots/${snapshotId}`);
  return data;
}

export async function restoreSnapshot(id: string, snapshotId: string): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>(
    `/analyses/${id}/snapshots/${snapshotId}/restore`,
  );
  return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function downloadBlob(response: { data: BlobPart; headers: any }, fallbackName: string): void {
  const disposition = String(response.headers['content-disposition'] || '');
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : fallbackName;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function exportAnalysisPdf(id: string): Promise<void> {
  const response = await api.get(`/analyses/${id}/export/pdf`, { responseType: 'blob' });
  downloadBlob(response, 'analysis_report.pdf');
}

export async function exportAnalysisXlsx(id: string): Promise<void> {
  const response = await api.get(`/analyses/${id}/export/xlsx`, { responseType: 'blob' });
  downloadBlob(response, 'analysis_report.xlsx');
}

export async function exportAnalysisCsv(id: string): Promise<void> {
  const response = await api.get(`/analyses/${id}/export/csv`, { responseType: 'blob' });
  downloadBlob(response, 'analysis_report.csv');
}

// ── AI Narrative ──

export async function checkNarrativeAvailable(): Promise<boolean> {
  try {
    const { data } = await api.get<{ available: boolean }>('/analyses/narrative-available');
    return data.available;
  } catch {
    return false;
  }
}

export async function generateNarrative(id: string): Promise<DealNarrative> {
  const { data } = await api.post<{ narrative: DealNarrative }>(
    `/analyses/${id}/generate-narrative`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.narrative;
}

// ── Disclosure Documents ──

export async function uploadDocument(
  analysisId: string,
  file: File,
  documentType: string = 'auto',
): Promise<{ document: DocumentInfo; extraction_status: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  const { data } = await api.post(`/analyses/${analysisId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function getDocuments(analysisId: string): Promise<DocumentInfo[]> {
  const { data } = await api.get<{ documents: DocumentInfo[] }>(
    `/analyses/${analysisId}/documents`,
  );
  return data.documents;
}

export async function deleteDocument(analysisId: string, docId: string): Promise<void> {
  await api.delete(`/analyses/${analysisId}/documents/${docId}`);
}

// ── Analysis Photos ──

export async function uploadAnalysisPhotos(
  analysisId: string,
  files: File[],
): Promise<{ analysis_id: string; photos_uploaded: number; photos: AnalysisPhotoInfo[] }> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const { data } = await api.post(`/analyses/${analysisId}/photos/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteAnalysisPhoto(analysisId: string, photoId: string): Promise<void> {
  await api.delete(`/analyses/${analysisId}/photos/${photoId}`);
}

// ── Condition Score ──

export async function checkConditionScoreAvailable(): Promise<boolean> {
  try {
    const { data } = await api.get<{ available: boolean }>('/analyses/condition-score-available');
    return data.available;
  } catch {
    return false;
  }
}

export async function generateConditionScore(id: string): Promise<PropertyConditionScore> {
  const { data } = await api.post<{ condition_score: PropertyConditionScore }>(
    `/analyses/${id}/generate-condition-score`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.condition_score;
}

// ── AI Comp Analysis ──

export async function checkCompAnalysisAvailable(): Promise<boolean> {
  try {
    const { data } = await api.get<{ available: boolean }>('/analyses/comp-analysis-available');
    return data.available;
  } catch {
    return false;
  }
}

export async function generateCompAnalysis(id: string): Promise<AICompAnalysis> {
  const { data } = await api.post<{ comp_analysis: AICompAnalysis }>(
    `/analyses/${id}/generate-comp-analysis`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.comp_analysis;
}

// ── Permit & Zoning Analysis ──

export async function checkPermitZoningAvailable(): Promise<boolean> {
  try {
    const { data } = await api.get<{ available: boolean }>('/analyses/permit-zoning-available');
    return data.available;
  } catch {
    return false;
  }
}

export async function generatePermitZoning(id: string): Promise<PermitZoningAnalysis> {
  const { data } = await api.post<{ permit_zoning: PermitZoningAnalysis }>(
    `/analyses/${id}/generate-permit-zoning`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.permit_zoning;
}

// ── Market Intelligence ──

export async function checkMarketIntelligenceAvailable(): Promise<boolean> {
  try {
    const { data } = await api.get<{ available: boolean }>(
      '/analyses/market-intelligence-available',
    );
    return data.available;
  } catch {
    return false;
  }
}

export async function generateMarketIntelligence(id: string): Promise<MarketIntelligenceAnalysis> {
  const { data } = await api.post<{ market_intelligence: MarketIntelligenceAnalysis }>(
    `/analyses/${id}/generate-market-intelligence`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.market_intelligence;
}

// ── Photo Scope Estimation ──

export async function estimateScopeFromPhotos(id: string): Promise<PhotoScopeEstimation> {
  const { data } = await api.post<{ estimation: PhotoScopeEstimation }>(
    `/analyses/${id}/scope-from-photos`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data.estimation;
}

// ── Lender Package ──

export async function generateLenderPackage(
  id: string,
  options?: { preparedFor?: string; companyName?: string; sections?: string[] },
): Promise<void> {
  const response = await api.post(
    `/analyses/${id}/lender-package`,
    {
      prepared_for: options?.preparedFor || null,
      company_name: options?.companyName || null,
      sections: options?.sections || null,
    },
    { responseType: 'blob', timeout: AI_TIMEOUT },
  );
  downloadBlob(response, 'lender_package.pdf');
}

// ── Portfolio ──

export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  const { data } = await api.get<PortfolioSummary>('/analyses/portfolio-summary');
  return data;
}

// ── Benchmarks ──

export async function getBenchmarks(): Promise<BenchmarkResponse> {
  const { data } = await api.get<BenchmarkResponse>('/analyses/benchmarks');
  return data;
}

// ── Buildability ──

export async function getBuildability(id: string): Promise<BuildabilityResponse> {
  const { data } = await api.get<BuildabilityResponse>(`/analyses/${id}/buildability`);
  return data;
}

export async function updateCostEstimates(
  id: string,
  estimates: CostEstimateResult,
): Promise<AnalysisResponse> {
  const { data } = await api.put<AnalysisResponse>(`/analyses/${id}/cost-estimates`, estimates);
  return data;
}

// ── Staleness ──

export async function dismissStaleness(id: string): Promise<void> {
  await api.post(`/analyses/${id}/dismiss-staleness`);
}

// ── Validation ──

export async function runValidation(id: string): Promise<unknown> {
  const { data } = await api.post(`/analyses/${id}/validate`);
  return data;
}

export async function getValidation(id: string): Promise<unknown> {
  const { data } = await api.get(`/analyses/${id}/validation`);
  return data;
}

// ── Comp Refresh ──

export async function acceptCompRefresh(id: string): Promise<AnalysisResponse> {
  const { data } = await api.post<AnalysisResponse>(`/analyses/${id}/accept-refresh`);
  return data;
}

// ── Flip Intelligence ──

export async function getFlipIntelligence(id: string): Promise<FlipIntelligenceStats> {
  const { data } = await api.get<FlipIntelligenceStats>(`/analyses/${id}/flip-intelligence`);
  return data;
}

// ── Deal Notes ──

export async function updateNotes(id: string, notes: string): Promise<{ notes: string | null }> {
  const { data } = await api.patch<{ notes: string | null }>(`/analyses/${id}/notes`, { notes });
  return data;
}

export async function updateNickname(
  id: string,
  nickname: string,
): Promise<{ nickname: string | null }> {
  const { data } = await api.patch<{ nickname: string | null }>(`/analyses/${id}/nickname`, {
    nickname,
  });
  return data;
}

export async function bulkDeleteAnalyses(ids: string[]): Promise<{ deleted: number }> {
  const { data } = await api.post<{ deleted: number }>('/analyses/bulk-delete', { ids });
  return data;
}

export async function bulkArchiveAnalyses(
  ids: string[],
  isArchived: boolean,
): Promise<{ updated: number }> {
  const { data } = await api.post<{ updated: number }>('/analyses/bulk-archive', {
    ids,
    is_archived: isArchived,
  });
  return data;
}
